/**
 * Robust Edge Function invoker that extracts real error messages
 * from non-2xx responses instead of showing generic errors.
 */
import { supabase } from "@/lib/supabase";

interface InvokeResult<T = any> {
  data: T | null;
  error: string | null;
  /** Machine-readable discriminator when the function sends one alongside
   *  `error`. The message is prose and changes; this does not. */
  code?: string | null;
}

export async function invokeFunction<T = any>(
  name: string,
  body: Record<string, unknown>
): Promise<InvokeResult<T>> {
  const { data, error } = await supabase.functions.invoke(name, { body });

  if (error) {
    // Try to extract the real error message from the response body
    try {
      const ctx = await (error as any).context?.json?.();
      if (ctx?.error) return { data: null, error: ctx.error, code: ctx.code ?? null };
    } catch {
      // context not available or not JSON
    }
    return { data: null, error: error.message };
  }

  // Edge function returned 200 but with an error field
  if (data?.error) {
    return { data: null, error: data.error, code: data.code ?? null };
  }

  return { data: data as T, error: null };
}
